// BUILD-046 task 8.20 (supporting model): the Caller summary (`GET /work/me`) as the My Work HOST needs it - with its FAILURE (design "Views and
// shell"; Req 1.3, 2.5, 2.7, 2.12).
//
// The shell hook (components/sharedTasks/useWorkMe.ts) swallows a failure, which is right for a nav item. The host cannot: when the call fails
// (the new routes are disabled, no identity, no connection) the page must stay exactly the legacy page (the two-way toggle for the existing
// manager gate, the legacy Team Work list, the legacy complete control), and the simulated-identity banner must still show in the local pilot.
// So the host keeps three phases:
//   loading : not answered yet (the page shows My Work only; no switcher, so nothing flickers in and out)
//   ready   : the Caller summary, used for the three-way switcher, the Assign task button, Done and the action controls
//   error   : the call failed; the page behaves as the legacy page (this is NOT shown as an error on the page: it is the routes-disabled case)
// A Caller change requests the summary again at once under the new epoch (the previous Caller's summary is cleared, never kept), and an answer
// for an older request or an older Caller is dropped (Req 2.7).
//
// Framework-free so it can be tested without a DOM; useHostMe.ts wraps it for React. Erasable TypeScript; no runtime imports.

export type HostMePhase = 'loading' | 'ready' | 'error';

export interface HostMeValue {
  employee: { employeeId: string; displayName: string; department?: string };
  canOversee: boolean;
  companyOversight: boolean;
  /** May the Caller assign tasks (`GET /work/me`). Absent (an older server) is read as false by toolbarModel. */
  canAssign?: boolean;
  /** May the Caller search the limited job directory (`GET /work/me`, BUILD-046 J1). Absent (an older server) is read as false: no job field, column or filter. */
  canSearchJobs?: boolean;
  /** May the Caller search the limited customer directory (`GET /work/me`, BUILD-046 J1). Absent (an older server) is read as false: no customer field, column or filter. */
  canSearchCustomers?: boolean;
  /**
   * Does the platform say the Caller has Pricing app access (`GET /work/me`, BUILD-046 customer association). Advice for the UI only: it turns the "Not in the list?
   * Add it in Pricing" note into a link. Absent (an older server) is read as false: the plain note, never the link.
   */
  canAddCustomerInPricing?: boolean;
  /**
   * What the server could tell about the Caller's Pricing access (`GET /work/me`, BUILD-046 customer association): allowed | not-allowed | unavailable (the check could not be made, which is NOT
   * "no access") | not-offered. Advice for the UI only. Absent (an older host summary) means the dialog decides from `canAddCustomerInPricing` alone, as before.
   */
  pricingCustomerAccess?: 'allowed' | 'not-allowed' | 'unavailable' | 'not-offered';
  canAdministerGroups: boolean;
  simulated: boolean;
  identity?: { simulated: boolean; label: string };
}

export interface HostMeState {
  phase: HostMePhase;
  /** The position of the latest accepted request: the Caller epoch, then the request number within it. */
  epoch: number;
  seq: number;
  /** Non-null only in `ready`. */
  me: HostMeValue | null;
  /** The failure message; non-null only in `error`. */
  message: string | null;
}

export function initialHostMe(): HostMeState {
  return { phase: 'loading', epoch: 0, seq: 0, me: null, message: null };
}

function isPosition(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isNewer(state: HostMeState, epoch: number, seq: number): boolean {
  return epoch > state.epoch || (epoch === state.epoch && seq > state.seq);
}

export type HostMeEvent =
  | { type: 'request'; epoch: number; seq: number }
  | { type: 'success'; epoch: number; seq: number; me: HostMeValue }
  | { type: 'failure'; epoch: number; seq: number; message: string };

export function hostMeReducer(state: HostMeState, event: HostMeEvent): HostMeState {
  if (!isPosition(event.epoch) || !isPosition(event.seq)) return state;
  if (event.type === 'request') {
    if (!isNewer(state, event.epoch, event.seq)) return state;
    return { phase: 'loading', epoch: event.epoch, seq: event.seq, me: null, message: null };
  }
  // An answer counts only for the request that is still the latest, and only once.
  if (state.phase !== 'loading' || event.epoch !== state.epoch || event.seq !== state.seq) return state;
  if (event.type === 'failure') return { phase: 'error', epoch: state.epoch, seq: state.seq, me: null, message: event.message };
  const employee = event.me?.employee;
  // A summary with no Employee id is not a summary: it is a failure, never a Caller with no identity.
  if (employee === undefined || employee === null || typeof employee.employeeId !== 'string' || employee.employeeId === '') {
    return { phase: 'error', epoch: state.epoch, seq: state.seq, me: null, message: 'The server answered, but not in the expected form.' };
  }
  return { phase: 'ready', epoch: state.epoch, seq: state.seq, me: event.me, message: null };
}

export interface HostMeControllerDeps {
  getEpoch: () => number;
  /** `workApi.getMe`. */
  getMe: () => Promise<HostMeValue>;
}

export interface HostMeController {
  getState: () => HostMeState;
  subscribe: (listener: () => void) => () => void;
  /** Requests the summary (mount, or after a failure). */
  load: () => Promise<void>;
  /** A Caller change: the previous summary is cleared at once and the new Caller's is requested. */
  callerChanged: () => Promise<void>;
}

export function createHostMeController(deps: HostMeControllerDeps): HostMeController {
  let state = initialHostMe();
  const listeners = new Set<() => void>();
  const publish = (next: HostMeState): void => {
    if (next === state) return;
    state = next;
    for (const listener of [...listeners]) listener();
  };

  async function load(): Promise<void> {
    const epoch = deps.getEpoch();
    const seq = state.epoch === epoch ? state.seq + 1 : 1;
    publish(hostMeReducer(state, { type: 'request', epoch, seq }));
    let me: HostMeValue;
    try {
      me = await deps.getMe();
    } catch (thrown) {
      // Dropped because the Caller changed: neither a summary nor a failure. The Caller change has already requested it again.
      if (thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale') return;
      const message = thrown !== null && typeof thrown === 'object' && typeof (thrown as { message?: unknown }).message === 'string' ? (thrown as { message: string }).message : '';
      publish(hostMeReducer(state, { type: 'failure', epoch, seq, message: message === '' ? 'The Caller summary could not be loaded.' : message }));
      return;
    }
    publish(hostMeReducer(state, { type: 'success', epoch, seq, me }));
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load,
    callerChanged: load,
  };
}
